import { deploymentProfile } from '../config/deploymentProfile';

import { useRef, useState, useEffect, useMemo } from 'react';
import ChartPreview from './ChartPreview';
import ErrorBoundary from './ErrorBoundary';
import DataImportModal from './DataImportModal';
import DataFilterPanel from './DataFilterPanel';
import EditableDataTable from './EditableDataTable';
import ColumnMappingPanel from './ColumnMappingPanel';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import { useNotification } from '../contexts/NotificationContext';
import { generateReactComponent } from '../utils/generateReactComponent';
import transformData from '../utils/transformData';
import { buildEmbeddedFontFaceCss, collectChartFontFamilies } from '../utils/embedFontsForExport';

const EXPORT_PADDING = 18;

function rawDataToGrid(rawData, columns) {
  const headers = columns.map((c) => c.name);
  const rows = rawData.map((row) => headers.map((h) => row[h] !== null && row[h] !== undefined ? String(row[h]) : ''));
  return [headers, ...rows];
}

function gridToRawData(grid) {
  const headers = grid[0].filter((h) => h.trim() !== '');
  const rows = grid.slice(1)
    .filter((row) => row.some((cell) => String(cell).trim() !== ''))
    .map((row) => {
      const obj = {};
      headers.forEach((h, i) => {
        const raw = String(row[i] ?? '').trim();
        const num = Number(raw);
        obj[h] = raw !== '' && !isNaN(num) ? num : raw;
      });
      return obj;
    });
  const sampleSize = Math.min(20, rows.length);
  const columns = headers.map((name) => {
    let numericCount = 0;
    let validCount = 0;
    for (let i = 0; i < sampleSize; i++) {
      const val = rows[i]?.[name];
      if (val !== null && val !== undefined && String(val).trim() !== '') {
        validCount++;
        if (!isNaN(Number(val))) numericCount++;
      }
    }
    const type = validCount > 0 && numericCount / validCount > 0.8 ? 'number' : 'string';
    return { name, type };
  });
  return { rows, columns };
}

function EditDataModal({ rawData, columns, onSave, onClose }) {
  const { resolved: theme } = useTheme();
  const ui = theme.ui || {};
  const [grid, setGrid] = useState(() => rawDataToGrid(rawData, columns));

  const handleSave = () => {
    const { rows, columns: newCols } = gridToRawData(grid);
    if (rows.length === 0) return;
    onSave(rows, newCols);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: ui.overlayBackdrop }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="border max-h-[90vh] flex flex-col"
        style={{ width: 660, backgroundColor: ui.bg, borderColor: ui.border }}
      >
        <div
          className="h-12 px-4 border-b flex items-center justify-between flex-shrink-0"
          style={{ borderBottomColor: ui.border }}
        >
          <span className="text-xs font-medium" style={{ color: ui.text }}>Edit Data</span>
          <button
            onClick={onClose}
            className="text-sm w-7 h-7 flex items-center justify-center border border-transparent ui-hover"
            style={{ color: ui.text }}
          >
            ×
          </button>
        </div>
        <div className="p-4 flex-1 overflow-y-auto flex flex-col gap-4">
          <EditableDataTable grid={grid} onChange={setGrid} />
          <div className="flex gap-2 pt-1 flex-shrink-0">
            <button
              onClick={onClose}
              className="flex-1 text-xs border py-2 ui-hover ui-border"
              style={{ borderColor: ui.border, color: ui.text }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex-1 text-xs border py-2 font-medium ui-hover ui-border"
              style={{ borderColor: ui.border, color: ui.text }}
            >
              Save Changes →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfigureColumnsModal({ rawData, columns, mapping, chartId, onSave, onClose }) {
  const { resolved: theme } = useTheme();
  const ui = theme.ui || {};

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: ui.overlayBackdrop }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="border max-h-[90vh] flex flex-col"
        style={{ width: 660, backgroundColor: ui.bg, borderColor: ui.border }}
      >
        <div
          className="h-12 px-4 border-b flex items-center justify-between flex-shrink-0"
          style={{ borderBottomColor: ui.border }}
        >
          <span className="text-xs font-medium" style={{ color: ui.text }}>Configure Columns</span>
          <button
            onClick={onClose}
            className="text-sm w-7 h-7 flex items-center justify-center border border-transparent ui-hover"
            style={{ color: ui.text }}
          >
            ×
          </button>
        </div>
        <div className="p-4 flex-1 overflow-y-auto">
          <ColumnMappingPanel
            columns={columns}
            rows={rawData}
            selectedChartId={chartId}
            existingMapping={mapping}
            onConfirm={(newMapping) => { onSave(newMapping); onClose(); }}
            onBack={onClose}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Clone and prepare an SVG element for export.
 * Injects a background <rect>, sets xmlns attributes, and ensures viewBox/width/height are present.
 */
/**
 * Parse an SVG path `d` attribute and return its axis-aligned bounding box.
 * Handles M, L, Q, C, H, V commands (absolute only — our paths are absolute).
 */
function parseBoundsFromPathD(d) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const re = /([MLQCSAHVZ])\s*([-\d.,e+\s]*)/gi;
  let m;
  while ((m = re.exec(d)) !== null) {
    const cmd = m[1].toUpperCase();
    if (cmd === 'Z') continue;
    const nums = m[2].trim().split(/[\s,]+/).map(Number).filter((n) => !isNaN(n));
    if (cmd === 'H') {
      for (let j = 0; j < nums.length; j++) {
        if (nums[j] < minX) minX = nums[j];
        if (nums[j] > maxX) maxX = nums[j];
      }
    } else if (cmd === 'V') {
      for (let j = 0; j < nums.length; j++) {
        if (nums[j] < minY) minY = nums[j];
        if (nums[j] > maxY) maxY = nums[j];
      }
    } else {
      for (let i = 0; i < nums.length - 1; i += 2) {
        const px = nums[i], py = nums[i + 1];
        if (px < minX) minX = px; if (px > maxX) maxX = px;
        if (py < minY) minY = py; if (py > maxY) maxY = py;
      }
    }
  }
  if (!isFinite(minX)) return null;
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function getElBounds(el) {
  const tag = el.tagName?.toLowerCase();
  if (tag === 'rect') {
    return {
      x: parseFloat(el.getAttribute('x') || '0'),
      y: parseFloat(el.getAttribute('y') || '0'),
      w: parseFloat(el.getAttribute('width') || '0'),
      h: parseFloat(el.getAttribute('height') || '0'),
    };
  }
  if (tag === 'path') return parseBoundsFromPathD(el.getAttribute('d') || '');
  return null;
}

function getSvgRootDefs(svgEl) {
  return Array.from(svgEl.children).find((child) => child.tagName?.toLowerCase() === 'defs') || null;
}

function ensureRootDefs(svgEl, ns) {
  let defs = getSvgRootDefs(svgEl);
  if (defs) return defs;
  defs = document.createElementNS(ns, 'defs');
  svgEl.insertBefore(defs, svgEl.firstChild || null);
  return defs;
}

function insertBackgroundRect(svgEl, ns, width, height, fill) {
  const bgRect = document.createElementNS(ns, 'rect');
  bgRect.setAttribute('x', '0');
  bgRect.setAttribute('y', '0');
  bgRect.setAttribute('width', String(width));
  bgRect.setAttribute('height', String(height));
  bgRect.setAttribute('fill', fill || '#FFFFFF');
  const defs = getSvgRootDefs(svgEl);
  svgEl.insertBefore(bgRect, defs ? defs.nextSibling : svgEl.firstChild || null);
  return bgRect;
}

function createExportContentGroup(svgEl, ns) {
  const contentGroup = document.createElementNS(ns, 'g');
  contentGroup.setAttribute('data-coplot-export-content', 'true');
  const movableNodes = Array.from(svgEl.childNodes).filter((node) => (
    !(node.nodeType === 1 && node.tagName?.toLowerCase() === 'defs')
  ));
  movableNodes.forEach((node) => contentGroup.appendChild(node));
  svgEl.appendChild(contentGroup);
  return contentGroup;
}

function measureExportContentBounds(svgEl, contentGroup, fallbackWidth, fallbackHeight) {
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-10000px';
  host.style.top = '0';
  host.style.visibility = 'hidden';
  host.style.pointerEvents = 'none';
  host.style.overflow = 'visible';
  host.style.width = '0';
  host.style.height = '0';

  document.body.appendChild(host);
  host.appendChild(svgEl);

  try {
    const bbox = contentGroup.getBBox();
    if (
      Number.isFinite(bbox.x) &&
      Number.isFinite(bbox.y) &&
      Number.isFinite(bbox.width) &&
      Number.isFinite(bbox.height) &&
      bbox.width >= 0 &&
      bbox.height >= 0
    ) {
      return bbox;
    }
  } catch (err) {
    // Fall through to the fixed-size fallback when getBBox is unavailable.
  } finally {
    host.remove();
  }

  return { x: 0, y: 0, width: fallbackWidth, height: fallbackHeight };
}

function fitExportToContent(svgEl, contentGroup, ns, bgColor, originalWidth, originalHeight, padding = EXPORT_PADDING) {
  const bbox = measureExportContentBounds(svgEl, contentGroup, originalWidth, originalHeight);
  const shiftX = bbox.x < padding ? padding - bbox.x : 0;
  const shiftY = bbox.y < padding ? padding - bbox.y : 0;
  const exportWidth = Math.max(
    originalWidth + shiftX,
    bbox.x + shiftX + bbox.width + padding,
  );
  const exportHeight = Math.max(
    originalHeight + shiftY,
    bbox.y + shiftY + bbox.height + padding,
  );

  if (shiftX !== 0 || shiftY !== 0) {
    contentGroup.setAttribute('transform', `translate(${shiftX}, ${shiftY})`);
  }

  svgEl.setAttribute('viewBox', `0 0 ${exportWidth} ${exportHeight}`);
  svgEl.setAttribute('width', String(exportWidth));
  svgEl.setAttribute('height', String(exportHeight));
  insertBackgroundRect(svgEl, ns, exportWidth, exportHeight, bgColor);

  return { width: exportWidth, height: exportHeight };
}

/**
 * Replace every `fill="url(#pattern-id)"` reference with directly-tiled copies
 * of the pattern's mark elements. This eliminates <pattern> dependencies so
 * the exported SVG renders identically in viewers that lack pattern support
 * (macOS Preview, many design tools, Slack/Discord previews, etc.).
 */
function bakePatternFills(svgEl, ns) {
  const defs = ensureRootDefs(svgEl, ns);
  const patterns = {};
  svgEl.querySelectorAll('pattern').forEach((p) => { if (p.id) patterns[p.id] = p; });

  Array.from(svgEl.querySelectorAll('[fill]')).forEach((el, idx) => {
    const fill = el.getAttribute('fill') || '';
    const ref = fill.match(/^url\(#(.+)\)$/);
    if (!ref) return;

    const pat = patterns[ref[1]];
    if (!pat) return;

    const tw = parseFloat(pat.getAttribute('width')) || 8;
    const th = parseFloat(pat.getAttribute('height')) || 8;
    const b = getElBounds(el);
    if (!b || b.w <= 0 || b.h <= 0) return;

    const marks = Array.from(pat.children).filter((c) => {
      if (c.tagName?.toLowerCase() !== 'rect') return true;
      return !(parseFloat(c.getAttribute('width')) >= tw && parseFloat(c.getAttribute('height')) >= th);
    });
    if (marks.length === 0) { el.setAttribute('fill', 'none'); return; }

    const clipId = `coplot-export-clip-${ref[1]}-${idx}`;
    const clipPath = document.createElementNS(ns, 'clipPath');
    clipPath.setAttribute('id', clipId);
    clipPath.setAttribute('clipPathUnits', 'userSpaceOnUse');
    const clipShape = el.cloneNode(true);
    clipShape.removeAttribute('fill');
    clipShape.removeAttribute('stroke');
    clipShape.removeAttribute('filter');
    clipShape.removeAttribute('mask');
    clipShape.removeAttribute('clip-path');
    clipPath.appendChild(clipShape);
    defs.appendChild(clipPath);

    const wrapper = document.createElementNS(ns, 'g');
    wrapper.setAttribute('clip-path', `url(#${clipId})`);
    const opacity = el.getAttribute('opacity');
    if (opacity != null) wrapper.setAttribute('opacity', opacity);
    const cols = Math.ceil(b.w / tw) + 2;
    const rows = Math.ceil(b.h / th) + 2;
    const startX = b.x - tw;
    const startY = b.y - th;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const tile = document.createElementNS(ns, 'g');
        tile.setAttribute('transform', `translate(${startX + c * tw},${startY + r * th})`);
        marks.forEach((mark) => tile.appendChild(mark.cloneNode(true)));
        wrapper.appendChild(tile);
      }
    }

    el.parentNode.insertBefore(wrapper, el.nextSibling);
    el.setAttribute('fill', 'none');
  });
}

function normalizeSvgDefs(svgEl, ns) {
  const allDefs = Array.from(svgEl.querySelectorAll('defs'));
  if (allDefs.length <= 1 && allDefs[0]?.parentNode === svgEl) return;

  const rootDefs = document.createElementNS(ns, 'defs');
  const seenIds = new Set();

  allDefs.forEach((defsNode) => {
    Array.from(defsNode.children).forEach((child) => {
      const id = child.getAttribute('id');
      if (id && seenIds.has(id)) return;
      if (id) seenIds.add(id);
      rootDefs.appendChild(child.cloneNode(true));
    });
  });

  allDefs.forEach((defsNode) => defsNode.remove());
  if (rootDefs.children.length > 0) {
    // Keep defs near the top-level so strict SVG consumers resolve url(#id) consistently.
    svgEl.insertBefore(rootDefs, svgEl.firstChild?.nextSibling || null);
  }
}

function resolveFontStack(fontFamily) {
  const defaultStack = deploymentProfile.font.reactFallbackStack;
  if (!fontFamily || !String(fontFamily).trim()) return defaultStack;
  const value = String(fontFamily).trim();
  if (/(sans-serif|serif|monospace|system-ui)/i.test(value)) return value;
  return `${value}, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;
}

/**
 * Extract title position and style from the chart container's HTML overlay.
 * All preview components render the title as an absolutely-positioned <div>
 * sibling of the <svg>. We measure its position relative to the SVG so we
 * can inject a matching <text> element into the SVG clone at export time.
 */
function extractTitleInfo(svgEl) {
  const container = svgEl?.parentElement;
  if (!container) return null;

  const titleDiv = Array.from(container.children).find(
    (el) => el.tagName === 'DIV' && el.style.position === 'absolute' && el.textContent?.trim()
  );
  if (!titleDiv) return null;

  const svgRect = svgEl.getBoundingClientRect();
  const titleRect = titleDiv.getBoundingClientRect();
  const computed = getComputedStyle(titleDiv);
  const fontSize = parseFloat(computed.fontSize) || 18;

  const svgW = parseFloat(svgEl.getAttribute('width')) || svgRect.width;
  const svgH = parseFloat(svgEl.getAttribute('height')) || svgRect.height;
  const scaleX = svgRect.width > 0 ? svgW / svgRect.width : 1;
  const scaleY = svgRect.height > 0 ? svgH / svgRect.height : 1;

  return {
    text: titleDiv.textContent,
    x: (titleRect.left - svgRect.left + titleRect.width / 2) * scaleX,
    y: (titleRect.top - svgRect.top) * scaleY + fontSize * 0.85,
    fontSize,
    fontWeight: computed.fontWeight || '600',
    color: computed.color || '#000000',
    fontFamily: computed.fontFamily,
  };
}

function prepareSVGClone(svgEl, bgColor, fontFamily, titleInfo, options = {}) {
  const fontFaceCss = options.fontFaceCss || '';
  const cloned = svgEl.cloneNode(true);
  const ns = 'http://www.w3.org/2000/svg';
  const w = parseFloat(svgEl.getAttribute('width') || String(svgEl.viewBox?.baseVal?.width || 720));
  const h = parseFloat(svgEl.getAttribute('height') || String(svgEl.viewBox?.baseVal?.height || 480));
  const resolvedFont = resolveFontStack(fontFamily);

  cloned.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  cloned.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  if (!cloned.getAttribute('viewBox')) {
    cloned.setAttribute('viewBox', `0 0 ${w} ${h}`);
  }
  // Explicit width/height needed so the browser knows the canvas draw size
  cloned.setAttribute('width', w);
  cloned.setAttribute('height', h);
  // Strip the in-app responsive "width:100%;height:100%" CSS — it overrides
  // the pixel attributes above and leaves standalone SVGs with ambiguous sizing.
  cloned.style.removeProperty('width');
  cloned.style.removeProperty('height');
  cloned.removeAttribute('overflow');
  cloned.setAttribute('font-family', resolvedFont);
  cloned.style.fontFamily = resolvedFont;

  // Ensure all text nodes inherit the expected app font in exported assets.
  cloned.querySelectorAll('text, tspan').forEach((node) => {
    if (!node.getAttribute('font-family')) {
      node.setAttribute('font-family', resolvedFont);
    }
  });
  normalizeSvgDefs(cloned, ns);

  // Inject font metadata without overriding role-specific font-family attributes.
  // The base64-embedded @font-face rules (fontFaceCss) make the exported asset
  // self-contained so it rasterizes/renders with the correct fonts outside the
  // live page context (see embedFontsForExport.js).
  const defs = ensureRootDefs(cloned, ns);
  const styleEl = document.createElementNS(ns, 'style');
  styleEl.textContent = `${fontFaceCss ? `${fontFaceCss}\n` : ''}svg { font-family: ${resolvedFont}; }`;
  defs.appendChild(styleEl);

  const contentGroup = createExportContentGroup(cloned, ns);

  if (titleInfo?.text) {
    const titleEl = document.createElementNS(ns, 'text');
    titleEl.setAttribute('x', String(titleInfo.x));
    titleEl.setAttribute('y', String(titleInfo.y));
    titleEl.setAttribute('font-size', String(titleInfo.fontSize));
    titleEl.setAttribute('font-weight', String(titleInfo.fontWeight));
    titleEl.setAttribute('fill', titleInfo.color);
    titleEl.setAttribute('font-family', titleInfo.fontFamily || resolvedFont);
    titleEl.setAttribute('text-anchor', 'middle');
    titleEl.textContent = titleInfo.text;
    contentGroup.appendChild(titleEl);
  }

  const { width, height } = fitExportToContent(
    cloned,
    contentGroup,
    ns,
    bgColor,
    w,
    h,
  );

  if (options.bakePatterns) {
    bakePatternFills(cloned, ns);
  }

  return { cloned, w: width, h: height };
}

function exportSVG(svgEl, bgColor, filename, fontFamily, titleInfo, fontFaceCss = '') {
  const { cloned } = prepareSVGClone(svgEl, bgColor, fontFamily, titleInfo, { bakePatterns: true, fontFaceCss });
  const serializer = new XMLSerializer();
  const svgString =
    '<?xml version="1.0" encoding="UTF-8"?>\n' + serializer.serializeToString(cloned);
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export PNG by rendering the SVG onto an offscreen canvas at the requested DPI.
 * Scale factor = dpi / 96 (96 px/in is the standard screen resolution).
 */
function exportPNG(svgEl, bgColor, filename, dpi, fontFamily, titleInfo, fontFaceCss = '') {
  const { cloned, w, h } = prepareSVGClone(svgEl, bgColor, fontFamily, titleInfo, { bakePatterns: false, fontFaceCss });
  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(cloned);
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const scale = dpi / 96;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);

  let rasterized = false;
  const rasterize = (img) => {
    if (rasterized) return;
    rasterized = true;
    ctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(url);
    const pngUrl = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = pngUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const img = new Image();
  img.onerror = () => {
    URL.revokeObjectURL(url);
  };
  // onload guarantees the SVG (with its base64-embedded fonts) is fully loaded.
  // decode() is attempted first for extra safety where supported.
  img.onload = () => rasterize(img);
  img.src = url;
  if (typeof img.decode === 'function') {
    img.decode().then(() => rasterize(img)).catch(() => {});
  }
}

const DPI_OPTIONS = [
  { value: 150, label: '150', hint: 'screen' },
  { value: 300, label: '300', hint: 'print' },
  { value: 600, label: '600', hint: 'publication' },
];

function PreviewPanel({ chartType, config, onUndo, onRedo, canUndo, canRedo, onReset, onResetStyles }) {
  const { resolved: theme } = useTheme();
  const { isLoaded, rawData, derivedData, columns, mapping, fileName, filterRules, sortRules, updateData, setMapping } = useData();
  const { addNotification } = useNotification();
  const previewRef = useRef(null);
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dpi, setDpi] = useState(300);
  const [dataModalOpen, setDataModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [configColumnsOpen, setConfigColumnsOpen] = useState(false);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);

  const hasActiveFilters = (filterRules?.length > 0) || (sortRules?.length > 0);

  const mappingStatus = useMemo(() => {
    if (!isLoaded || !rawData.length) return 'none';
    if (!mapping || Object.keys(mapping).length === 0) return 'empty';
    const result = transformData(rawData, mapping, chartType?.id);
    if (!result || (Array.isArray(result) && result.length === 0)) return 'invalid';
    return 'ok';
  }, [isLoaded, rawData, mapping, chartType?.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    function handleOutsideClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [menuOpen]);

  function getSVGEl() {
    return previewRef.current?.querySelector('svg') ?? null;
  }

  const handleExportSVG = async () => {
    const svgEl = getSVGEl();
    if (!svgEl) return;
    const titleInfo = extractTitleInfo(svgEl);
    const fontFaceCss = await buildEmbeddedFontFaceCss(
      collectChartFontFamilies(svgEl, theme, titleInfo)
    );
    exportSVG(svgEl, theme.background || '#FFFFFF', `${chartType?.id || 'chart'}.svg`, theme.fontBody, titleInfo, fontFaceCss);
    setMenuOpen(false);
  };

  const handleExportReactComponent = () => {
    if (!chartType) return;
    const jsx = generateReactComponent(chartType, config, theme, isLoaded, rawData, mapping);
    if (!jsx) {
      addNotification('React component export is not supported for this chart type yet.', 'warning');
      return;
    }
    const blob = new Blob([jsx], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${chartType.id}.jsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setMenuOpen(false);
  };

  const handleExportPNG = async () => {
    const svgEl = getSVGEl();
    if (!svgEl) return;
    const titleInfo = extractTitleInfo(svgEl);
    const fontFaceCss = await buildEmbeddedFontFaceCss(
      collectChartFontFamilies(svgEl, theme, titleInfo)
    );
    exportPNG(
      svgEl,
      theme.background || '#FFFFFF',
      `${chartType?.id || 'chart'}-${dpi}dpi.png`,
      dpi,
      theme.fontBody,
      titleInfo,
      fontFaceCss
    );
    setMenuOpen(false);
  };

  const ui = theme.ui || {};

  return (
    <div className="flex-1 min-h-0 flex flex-col border-b ui-border overflow-hidden" style={{ borderBottomColor: ui.border }}>
      {/* Toolbar */}
      <div
        className="flex-shrink-0 flex items-center justify-end gap-2 px-4 h-12 border-b ui-bg ui-border"
        style={{ backgroundColor: ui.bg, borderBottomColor: ui.border }}
      >
        {isLoaded && (
          <span className="text-xs mr-auto truncate max-w-[200px]" style={{ color: ui.textSecondary }} title={fileName}>
            {fileName || 'data'} · {rawData.length} row{rawData.length !== 1 ? 's' : ''}
          </span>
        )}
        <div className="flex gap-2 mr-2 border-r pr-4 ui-border" style={{ borderColor: ui.border }}>
          <button 
            onClick={onUndo} 
            disabled={!canUndo}
            className={`text-xs px-2 py-1 rounded ${canUndo ? 'hover:bg-[var(--ui-hover)] text-[var(--ui-text)]' : 'opacity-50 text-[var(--ui-textSecondary)] cursor-not-allowed'}`}
            title="Undo (Cmd/Ctrl + Z)"
          >
            Undo
          </button>
          <button 
            onClick={onRedo} 
            disabled={!canRedo}
            className={`text-xs px-2 py-1 rounded ${canRedo ? 'hover:bg-[var(--ui-hover)] text-[var(--ui-text)]' : 'opacity-50 text-[var(--ui-textSecondary)] cursor-not-allowed'}`}
            title="Redo (Cmd/Ctrl + Shift + Z)"
          >
            Redo
          </button>
        </div>
        {isLoaded && (
          <button
            onClick={() => setFilterPanelOpen(o => !o)}
            className="text-xs border px-3 py-1 ui-hover ui-border"
            style={{
              borderColor: hasActiveFilters ? ui.accent : ui.border,
              color: hasActiveFilters ? ui.accent : undefined,
            }}
            title="Filter & sort data"
          >
            {hasActiveFilters ? `⚡ Filter (${(filterRules?.length || 0) + (sortRules?.length || 0)})` : '⚡ Filter'}
          </button>
        )}
        {isLoaded && (
          <button
            onClick={() => setEditModalOpen(true)}
            className="text-xs border px-3 py-1 ui-hover ui-border"
            style={{ borderColor: ui.border }}
            title="Edit current data"
          >
            Edit Data
          </button>
        )}
        {isLoaded && (
          <button
            onClick={() => setConfigColumnsOpen(true)}
            className="text-xs border px-3 py-1 ui-hover ui-border"
            style={{ borderColor: ui.border }}
            title="Reassign columns to chart roles"
          >
            Configure Columns
          </button>
        )}
        <button
          onClick={onResetStyles}
          className="text-xs border px-3 py-1 ui-hover ui-border"
          style={{ borderColor: ui.border }}
          title="Reset all styles and labels to defaults (keeps data)"
        >
          Reset Styles
        </button>
        {isLoaded && (
          <button
            onClick={onReset}
            className="text-xs border px-3 py-1 ui-hover ui-border"
            style={{ borderColor: ui.border }}
            title="Clear data and reset chart to its default state"
          >
            Reset Chart
          </button>
        )}
        <button
          onClick={() => setDataModalOpen(true)}
          className="text-xs border px-3 py-1 ui-hover ui-border"
          style={{ borderColor: ui.border }}
          title="Import data"
        >
          {isLoaded ? '↑ Replace Data' : '↑ Add Data'}
        </button>
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="text-xs border px-3 py-1"
            style={{ backgroundColor: ui.accent, color: ui.accentFg, borderColor: ui.accent }}
            title="Export chart"
          >
            ↓ Export ▾
          </button>

          {menuOpen && (
            <div
              className="absolute right-0 top-full mt-0.5 border z-10 min-w-[172px] ui-bg ui-border"
              style={{ backgroundColor: ui.bg, borderColor: ui.border }}
            >
              <button
                onClick={handleExportSVG}
                className="w-full text-left text-xs px-3 py-2 border-b ui-hover ui-border"
                style={{ borderBottomColor: ui.border }}
              >
                Download SVG
              </button>

              <button
                onClick={handleExportReactComponent}
                className="w-full text-left text-xs px-3 py-2 border-b ui-hover ui-border"
                style={{ borderBottomColor: ui.border }}
              >
                Download React Component
              </button>

              <div className="px-3 py-2">
                <button
                  onClick={handleExportPNG}
                  className="text-xs hover:underline block mb-2"
                >
                  Download PNG
                </button>
                <div className="flex gap-1">
                  {DPI_OPTIONS.map(({ value, label, hint }) => (
                    <button
                      key={value}
                      onClick={() => setDpi(value)}
                      className={`text-xs px-2 py-0.5 border ui-border ${
                        dpi === value ? 'ui-accent-bg' : 'ui-hover'
                      }`}
                      style={{ borderColor: ui.border }}
                      title={`${value} DPI — ${hint}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="text-xs mt-1" style={{ color: ui.textSecondary }}>
                  {DPI_OPTIONS.find((o) => o.value === dpi)?.hint} · {dpi} DPI
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {(mappingStatus === 'empty' || mappingStatus === 'invalid') && (
        <div
          className="flex-shrink-0 flex items-center gap-3 px-4 py-2 border-b text-xs"
          style={{ backgroundColor: ui.bgSecondary, borderBottomColor: ui.border, color: ui.textSecondary }}
        >
          <span style={{ color: ui.text, fontWeight: 500 }}>
            {mappingStatus === 'empty' ? '⚠ Column mapping incomplete' : '⚠ Column mapping mismatch'}
          </span>
          <span>
            {mappingStatus === 'empty'
              ? 'Your data is loaded but we couldn\'t auto-detect the column roles.'
              : 'The current column mapping doesn\'t produce valid chart data. Try reassigning columns.'}
          </span>
          <button
            onClick={() => setConfigColumnsOpen(true)}
            className="ml-auto flex-shrink-0 text-xs border px-2 py-0.5 font-medium ui-hover"
            style={{ borderColor: ui.border, color: ui.text }}
          >
            Configure Columns →
          </button>
        </div>
      )}

      {filterPanelOpen && isLoaded && columns.length > 0 && (
        <DataFilterPanel derivedRowCount={derivedData?.length ?? rawData.length} />
      )}

      {/* Chart canvas */}
      <div
        ref={previewRef}
        className="flex-1 overflow-auto flex items-center justify-center p-6"
        style={{ backgroundColor: theme.background, fontFamily: theme.fontBody || 'Cohere Text' }}
      >
        <ErrorBoundary fallbackMessage="Chart couldn't render. Try adjusting your settings.">
          <ChartPreview chartType={chartType} config={config} />
        </ErrorBoundary>
      </div>

      {dataModalOpen && <DataImportModal onClose={() => setDataModalOpen(false)} selectedChartId={chartType?.id} />}
      {editModalOpen && isLoaded && (
        <EditDataModal
          rawData={rawData}
          columns={columns}
          onSave={updateData}
          onClose={() => setEditModalOpen(false)}
        />
      )}
      {configColumnsOpen && isLoaded && (
        <ConfigureColumnsModal
          rawData={rawData}
          columns={columns}
          mapping={mapping}
          chartId={chartType?.id}
          onSave={setMapping}
          onClose={() => setConfigColumnsOpen(false)}
        />
      )}
    </div>
  );
}

export default PreviewPanel;
